import React, { useState, useEffect } from "react";
import { FaTimes } from "react-icons/fa";

function EditClassModal({
  isEditOpen,
  setIsEditOpen,
  currentClass,
  handleEditClass,
}) {
  const [className, setClassName] = useState("");
  const [type, setType] = useState("");
  const [level, setLevel] = useState("");
  const [bg, setBg] = useState("");

  // Example BG images (1–8) with placeholders
  const bgImages = [
    { id: "1", label: "bgimg1", url: "/dashboard/bgimg1.png" },
    { id: "2", label: "bgimg2", url: "/dashboard/bgimg2.png" },
    { id: "3", label: "bgimg3", url: "/dashboard/bgimg3.png" },
    { id: "4", label: "bgimg4", url: "/dashboard/bgimg4.png" },
    { id: "5", label: "bgimg5", url: "/dashboard/bgimg5.png" },
    { id: "6", label: "bgimg6", url: "/dashboard/bgimg6.png" },
    { id: "7", label: "bgimg7", url: "/dashboard/bgimg7.png" },
    { id: "8", label: "bgimg8", url: "/dashboard/bgimg8.png" },
  ];

  // Load the current class fields into local states on modal open
  useEffect(() => {
    if (currentClass) {
      setClassName(currentClass.class_name || "");
      setType(currentClass.type || "");
      setLevel(currentClass.level ? String(currentClass.level) : "");
      setBg(currentClass.bg || "");
    }
  }, [currentClass]);

  // If not open, render nothing
  if (!isEditOpen) return null;

  // Submitting the form
  const onSubmit = (e) => {
    e.preventDefault();

    // Basic checks (optional)
    if (!className.trim()) {
      alert("Class name is required.");
      return;
    }
    if (!type) {
      alert("Please select a type.");
      return;
    }
    if (!level) {
      alert("Please select a level.");
      return;
    }
    if (!bg) {
      alert("Please select a background image.");
      return;
    }

    // Pass updated data to parent
    const updatedClassData = {
      ...currentClass,      // keep old fields (like classid)
      class_name: className,
      type,
      level,
      bg,
    };

    // We assume handleEditClass calls server & updates parent state
    handleEditClass(updatedClassData);

    // Close modal after success
    setIsEditOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
      <div className="relative bg-white w-full max-w-md rounded-lg shadow-xl p-6 mx-4">
        {/* Close button */}
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          onClick={() => setIsEditOpen(false)}
        >
          <FaTimes size={20} />
        </button>

        <h2 className="text-2xl font-bold text-gray-700 mb-4">Edit Class</h2>

        <form onSubmit={onSubmit}>
          {/* Class Name */}
          <label className="block mb-2 text-base font-medium text-gray-700">
            Class Name
          </label>
          <input
            type="text"
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-base mb-4"
          />

          {/* Type Select */}
          <label className="block mb-2 text-base font-medium text-gray-700">
            Select Type
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-base mb-4"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            <option value="">-- Please choose a type --</option>
            <option value="SAT">SAT</option>
            <option value="SAT 8/9">SAT 8/9</option>
            <option value="NMSQT">NMSQT</option>
            <option value="ACT">ACT</option>
            <option value="AP Calculus AB">AP Calculus AB</option>
            <option value="AP Calculus BC">AP Calculus BC</option>
          </select>

          {/* Level Select */}
          <label className="block mb-2 text-base font-medium text-gray-700">
            Select Level
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-base mb-4"
            value={level}
            onChange={(e) => setLevel(e.target.value)}
          >
            <option value="">-- Please choose a level --</option>
            <option value="1">Beginner</option>
            <option value="2">Intermediate</option>
            <option value="3">Advanced</option>
          </select>

          {/* BG selection */}
          <p className="mb-2 text-base font-medium text-gray-700">
            Choose a Background:
          </p>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {bgImages.map((item, idx) => (
              <label key={item.id} className="relative cursor-pointer group">
                <input
                  type="radio"
                  name="bg"
                  value={item.id}
                  className="absolute opacity-0"
                  checked={bg === item.id}
                  onChange={(e) => setBg(e.target.value)}
                />
                <div
                  className={`
                    w-20 h-20 rounded-md border-2 
                    overflow-hidden
                    group-hover:opacity-90 
                    ${
                      bg === item.id ? "border-blue-500" : "border-transparent"
                    }
                  `}
                >
                  <img
                    src={item.url}
                    alt={item.label}
                    className="object-cover w-full h-full"
                  />
                </div>
                <p
                  className={`
                    text-xs text-center mt-1
                    ${
                      bg === item.id
                        ? "font-bold text-gray-800"
                        : "font-normal text-gray-600"
                    }
                  `}
                >
                  BG #{idx + 1}
                </p>
              </label>
            ))}
          </div>

          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md transition-colors"
          >
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
}

export default EditClassModal;
